import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import gsap from 'gsap';

const HeroContainer = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2;
  padding: 0 2rem;
  overflow: hidden;
`;

const GlowingTitle = styled(motion.h1)`
  font-size: clamp(3rem, 10vw, 8rem);
  font-weight: 900;
  text-align: center;
  margin: 0;
  color: transparent;
  background: linear-gradient(90deg, #00BFFF, #A020F0, #FF00FF, #00BFFF);
  background-size: 400% 400%;
  background-clip: text;
  -webkit-background-clip: text;
  animation: gradient 8s ease infinite;
  filter: drop-shadow(0 0 25px rgba(112, 0, 255, 0.8));
  text-transform: uppercase;
  letter-spacing: -2px;
`;

const SubTitle = styled(motion.p)`
  font-size: clamp(1rem, 2vw, 1.5rem);
  font-weight: 400;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  margin: 1.5rem 0 3rem;
  letter-spacing: 3px;
  max-width: 700px;
`;

const GlitchText = styled.span`
  position: relative;
  display: inline-block;
  
  &:after, &:before {
    content: attr(data-text);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    color: white;
  }
  
  &:after {
    left: 2px;
    text-shadow: -1px 0 rgba(0, 191, 255, 0.8);
    animation: glitch-1 2s infinite linear alternate-reverse;
  }
  
  &:before {
    left: -2px;
    text-shadow: 1px 0 rgba(255, 0, 255, 0.8);
    animation: glitch-2 3s infinite linear alternate-reverse;
  }
  
  @keyframes glitch-1 {
    0% { clip-path: inset(20% 0 40% 0); }
    20% { clip-path: inset(60% 0 1% 0); }
    40% { clip-path: inset(40% 0 60% 0); }
    60% { clip-path: inset(80% 0 5% 0); }
    80% { clip-path: inset(15% 0 90% 0); }
    100% { clip-path: inset(50% 0 25% 0); }
  }
  
  @keyframes glitch-2 {
    0% { clip-path: inset(40% 0 20% 0); }
    20% { clip-path: inset(1% 0 60% 0); }
    40% { clip-path: inset(60% 0 40% 0); }
    60% { clip-path: inset(5% 0 80% 0); }
    80% { clip-path: inset(90% 0 15% 0); }
    100% { clip-path: inset(25% 0 50% 0); }
  }
`;

const InteractiveButton = styled(motion.button)`
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  background: linear-gradient(90deg, #00BFFF, #A020F0);
  border: none;
  border-radius: 30px;
  color: white;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(160, 32, 240, 0.4);
  transition: all 0.3s ease;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, rgba(0, 191, 255, 0.5), rgba(160, 32, 240, 0.5));
    z-index: -1;
    transition: all 0.3s ease;
    transform: translateX(-100%);
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(160, 32, 240, 0.5);
    
    &:before {
      transform: translateX(0);
    }
  }
`;

const HeroSection: React.FC = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  
  useEffect(() => {
    if (!titleRef.current) return;
    
    // Create a hover effect
    const handleMouseMove = (e: MouseEvent) => {
      if (!titleRef.current) return;
      
      const { left, top, width, height } = titleRef.current.getBoundingClientRect();
      const x = (e.clientX - left) / width;
      const y = (e.clientY - top) / height;
      
      gsap.to(titleRef.current, {
        filter: `drop-shadow(0 0 30px rgba(${Math.floor(255 * y)}, ${Math.floor(100 * x)}, ${Math.floor(255 * (1-x))}, 0.8))`,
        duration: 0.5,
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  const titleVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        duration: 0.8, 
        ease: "easeOut" 
      }
    }
  };
  
  const subtitleVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        duration: 0.8,
        delay: 0.3,
        ease: "easeOut" 
      }
    }
  };
  
  const buttonVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        duration: 0.8,
        delay: 0.6,
        ease: "easeOut" 
      }
    },
    tap: { scale: 0.95 }
  };
  
  return (
    <HeroContainer>
      <GlowingTitle 
        ref={titleRef}
        variants={titleVariants}
        initial="hidden"
        animate="visible"
      >
        <GlitchText data-text="Aetherion">Aetherion</GlitchText>
      </GlowingTitle>
      
      <SubTitle
        variants={subtitleVariants}
        initial="hidden"
        animate="visible"
      >
        TRANSCENDING DIGITAL EXPERIENCES • COMING SOON
      </SubTitle>
      
      <motion.div
        variants={buttonVariants}
        initial="hidden"
        animate="visible"
        whileTap="tap"
      >
        <InteractiveButton>
          Stay Updated
        </InteractiveButton>
      </motion.div>
    </HeroContainer>
  );
};

export default HeroSection; 